<template>
    <div id="telnyx-user-mapping">
        <div class="page-header">
            <div class="left">
                <h3>Kullanıcı Eşleme</h3>
                <p>Bu Telnyx numarasını kullanacak kullanıcıyı seçin</p>
            </div>
            <div class="right">
                <a-button type="primary" @click="showUserPicker = true" :icon="'user'">
                    Kullanıcı Değiştir
                </a-button>
            </div>
        </div>

        <!-- Current User Display -->
        <div class="current-user-section">
            <div class="section-title">Mevcut Atama</div>
            <div v-if="currentUser" class="user-card">
                <div class="user-info">
                    <a-avatar :size="64">
                        {{ getUserInitials(currentUser) }}
                    </a-avatar>
                    <div class="user-details">
                        <div class="user-name">{{ currentUser.name }}</div>
                        <div class="user-email">{{ currentUser.email }}</div>
                        <div class="user-phone">{{  $_.get(value, "payload.callerPhoneNumber", "Numara tanımlı değil") }}</div>
                    </div>
                </div>
                <div class="user-actions">
                    <a-tag color="green" v-if="currentUser._id === $_.get(value, 'payload.userId', null)">
                        <i class="material-icons" style="font-size: 14px; margin-right: 4px;">check_circle</i>
                        Aktif
                    </a-tag>
                    <a-button 
                        type="danger" 
                        size="small" 
                        @click="removeUserMapping"
                        style="margin-top: 8px;"
                    >
                        Eşlemeyi Kaldır
                    </a-button>
                </div>
            </div>
            <div v-else class="no-user-assigned">
                <div class="no-user-icon">
                    <i class="material-icons">person_off</i>
                </div>
                <div class="no-user-text">
                    <div class="title">Kullanıcı Atanmamış</div>
                    <div class="subtitle">Bu Telnyx numarası henüz hiç kimseye atanmamış</div>
                    <a-button type="primary" @click="showUserPicker = true" style="margin-top: 16px;">
                        Kullanıcı Ata
                    </a-button>
                </div>
            </div>
        </div>

        <!-- Channel Info -->
        <div class="channel-info-section">
            <div class="section-title">Kanal Bilgileri</div>
            <div class="info-grid">
                <div class="info-item">
                    <div class="label">Kanal Adı</div>
                    <div class="value">{{ value.name }}</div>
                </div>
                <div class="info-item">
                    <div class="label">Telefon Numarası</div>
                    <div class="value">{{ $_.get(value, "payload.callerPhoneNumber", "Numara tanımlı değil") }}</div>
                </div>
                <div class="info-item">
                    <div class="label">Username</div>
                    <div class="value">{{ $_.get(value, "payload.username", "Kullanıcı tanımlı değil") }}</div>
                </div>
                <div class="info-item">
                    <div class="label">Domain</div>
                    <div class="value">{{ $_.get(value, "payload.domain", "Domain tanımlı değil") }}</div>
                </div>
            </div>
        </div>

        <!-- User Picker Modal -->
        <a-modal
            v-model="showUserPicker"
            title="Kullanıcı Seç"
            @ok="handleUserSelection"
            @cancel="cancelUserSelection"
            :confirmLoading="saving"
            width="800px"
        >
            <UserPicker @user-selected="onUserSelected" />
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import UserPicker from "../../../Tables/detail/pickers/UserPicker.vue";
import log2 from "../../../../../utils/log2";

const log = log2("TelnyxUserMapping");

export default Vue.extend({
    name: "TelnyxUserMapping",
    components: {
        UserPicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            saving: false,
            showUserPicker: false,
            selectedUser: null,
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users?.data || [],
        }),
        currentUser() {
            if (!this.value.payload?.userId) return null;
            return this.users.find(user => user._id === this.value.payload.userId);
        },
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
            fetchUsers: "user/getUsers",
        }),
        onUserSelected(user) {
            this.selectedUser = user;
        },
        async handleUserSelection() {
            if (!this.selectedUser) {
                this.$message.error("Lütfen bir kullanıcı seçin");
                return;
            }

            try {
                this.saving = true;
                await this.saveUserMapping(this.selectedUser._id);
                this.$message.success("Kullanıcı eşlemesi başarıyla güncellendi");
                this.showUserPicker = false;
                this.selectedUser = null;
            } catch (error) {
                console.error("User mapping save error:", error);
                this.$message.error("Kullanıcı eşlemesi güncellenirken hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        cancelUserSelection() {
            this.showUserPicker = false;
            this.selectedUser = null;
        },
        async removeUserMapping() {
            try {
                this.saving = true;
                await this.saveUserMapping(null);
                this.$message.success("Kullanıcı eşlemesi kaldırıldı");
            } catch (error) {
                console.error("User mapping remove error:", error);
                this.$message.error("Kullanıcı eşlemesi kaldırılırken hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        async saveUserMapping(userId) {
            const updatedPayload = {
                ...this.value.payload,
                userId: userId,
            };

            const updatedChannel = {
                ...this.value,
                payload: updatedPayload,
            };

            // Update channel via API
            await this.updateChannel({
                _id: this.value._id,
                name: this.value.name,
                enabled: this.value.enabled,
                payload: updatedPayload,
                visibility: this.value.visibility,
                forward: this.value.forward,
                commentForward: this.value.commentForward,
                zohoForward: this.value.zohoForward,
            });

            // Emit the updated channel
            this.$emit("input", updatedChannel);

            log("User mapping saved:", { userId, channelId: this.value._id });
        },
        getUserInitials(user) {
            if (!user || !user.name) return "?";
            return user.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .substring(0, 2);
        },
    },
    async mounted() {
        // Load users
        await this.fetchUsers({
            perPage: 1000,
            page: 1,
            query: "",
            updateStore: true,
        });
    },
});
</script>

<style lang="scss" scoped>
#telnyx-user-mapping {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 30px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }
    }

    .current-user-section {
        margin-bottom: 30px;

        .section-title {
            font-size: 16px;
            font-weight: 600;
            color: var(--color-text-primary);
            margin-bottom: 16px;
        }

        .user-card {
            background: white;
            border-radius: 12px;
            padding: 24px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            border: 1px solid var(--color-border-default);
            display: flex;
            justify-content: space-between;
            align-items: center;

            .user-info {
                display: flex;
                align-items: center;
                gap: 20px;

                .user-details {
                    .user-name {
                        font-size: 18px;
                        font-weight: 600;
                        color: var(--color-text-primary);
                        margin-bottom: 4px;
                    }

                    .user-email {
                        font-size: 14px;
                        color: var(--color-text-secondary);
                        margin-bottom: 4px;
                    }

                    .user-phone {
                        font-size: 14px;
                        color: #1890ff;
                        font-weight: 500;
                    }
                }
            }

            .user-actions {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
            }
        }

        .no-user-assigned {
            background: #fafafa;
            border: 2px dashed #d9d9d9;
            border-radius: 12px;
            padding: 40px;
            text-align: center;

            .no-user-icon {
                margin-bottom: 16px;

                .material-icons {
                    font-size: 48px;
                    color: #bfbfbf;
                }
            }

            .no-user-text {
                .title {
                    font-size: 18px;
                    font-weight: 600;
                    color: var(--color-text-primary);
                    margin-bottom: 8px;
                }

                .subtitle {
                    font-size: 14px;
                    color: var(--color-text-secondary);
                }
            }
        }
    }

    .channel-info-section {
        .section-title {
            font-size: 16px;
            font-weight: 600;
            color: var(--color-text-primary);
            margin-bottom: 16px;
        }

        .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;

            .info-item {
                background: white;
                border-radius: 8px;
                padding: 16px;
                border: 1px solid var(--color-border-default);

                .label {
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--color-text-secondary);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 8px;
                }

                .value {
                    font-size: 14px;
                    font-weight: 500;
                    color: var(--color-text-primary);
                    word-break: break-all;
                }
            }
        }
    }
}
</style> 